/** 숫자만 추출 (서버 전송 시 사용) */
export function normalizePhone(phone?: string | null): string | null {
  if (!phone) {
    return null;
  }
  const normalized = phone.replace(/\D/g, '');
  return normalized || null;
}

/**
 * 저장된 전화번호를 표시용으로 포맷 (완성된 번호 대상)
 *
 * 1. 010-XXXX-XXXX : 11자리 휴대폰
 * 2. 010-XXX-XXXX  : 10자리 휴대폰 (구형)
 * 3. 02-XXXX-XXXX  : 10자리 서울 지역번호
 * 4. 031-XXX-XXXX  : 10자리 서울 외 지역번호
 * 5. 1688-XXXX     : 8자리 대표번호
 */
export function formatKoreanPhoneNumber(phone?: string | null): string {
  const normalized = normalizePhone(phone);
  if (!normalized) {
    return '';
  }

  // 11자리: 010-XXXX-XXXX
  if (normalized.length === 11) {
    return normalized.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  }

  // 8자리: 1688-XXXX
  if (normalized.length === 8) {
    return normalized.replace(/(\d{4})(\d{4})/, '$1-$2');
  }

  // 02로 시작: 02-XXXX-XXXX
  if (normalized.startsWith('02')) {
    return normalized.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
  }

  // 나머지 10자리: 031-XXX-XXXX / 010-XXX-XXXX
  const result = normalized.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  return result !== normalized ? result : normalized;
}

/** 서버 전송 시 하이픈 제거 (normalizePhone alias) */
export const stripPhone = (value: string): string =>
  normalizePhone(value) ?? value;

/**
 * 입력 필드에서 실시간 포맷 (타이핑 중 사용)
 * - 완성 시점에 formatKoreanPhoneNumber와 동일한 패턴으로 확정
 */
export const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '');

  // 서울 지역번호: 02-XXXX-XXXX (10자리)
  if (digits.startsWith('02')) {
    const sliced = digits.slice(0, 10);
    if (sliced.length <= 2) {
      return sliced;
    }
    if (sliced.length <= 6) {
      return sliced.replace(/(\d{2})(\d+)/, '$1-$2');
    }
    // 7–9자리 중간: 2-4-X
    if (sliced.length <= 9) {
      return sliced.replace(/(\d{2})(\d{4})(\d+)/, '$1-$2-$3');
    }
    // 10자리 완성: 02-XXXX-XXXX
    return sliced.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
  }

  // 대표번호: 1688-XXXX 등 (0으로 시작하지 않는 최대 8자리)
  if (!digits.startsWith('0')) {
    const sliced = digits.slice(0, 8);
    if (sliced.length <= 4) {
      return sliced;
    }
    return sliced.replace(/(\d{4})(\d+)/, '$1-$2');
  }

  // 휴대폰·지역번호
  const sliced = digits.slice(0, 11);
  if (sliced.length <= 3) {
    return sliced;
  }
  if (sliced.length <= 7) {
    return sliced.replace(/(\d{3})(\d+)/, '$1-$2');
  }
  // 8–9자리 중간: 3-3-X
  if (sliced.length <= 9) {
    return sliced.replace(/(\d{3})(\d{3})(\d+)/, '$1-$2-$3');
  }
  // 10자리 완성: 031-XXX-XXXX / 010-XXX-XXXX (3-3-4)
  if (sliced.length === 10) {
    return sliced.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  }
  // 11자리 완성: 010-XXXX-XXXX (3-4-4)
  return sliced.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
};
